import { useTranslation } from 'next-i18next';
import type { ChangeEvent, DragEvent } from 'react';
import { useEffect, useState } from 'react';
import { useBodyDragOver } from './useBodyDragOver';
import { type ToastPosition, ToastType, useToasts } from '@hub/hooks/useToasts';
import { APP_NAME } from '@hub/libs/constants';
import { Loader } from '@hub/free-form-hub/common/loader/Loader';
import { Trans } from 'react-i18next';

type DropzoneProps = {
  clientReady: boolean;
  onDrop: (file: File) => void;
  onShowSampleModel?: () => void;
  fullScreen?: boolean;
  maxFileSize?: number;
  uploadExplanationText?: boolean;
  sampleModels?: boolean;
  showToastOnError?: boolean;
  isLoading?: boolean;
};

export const DropZone = ({
  fullScreen,
  onDrop,
  onShowSampleModel,
  maxFileSize,
  uploadExplanationText = true,
  sampleModels = true,
  showToastOnError = true,
  isLoading,
  clientReady,
}: DropzoneProps) => {
  const { t } = useTranslation('free-form-hub/freeFormHubCommon');
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const maxSize = maxFileSize || 75; // in MB
  const { addToast } = useToasts();

  useBodyDragOver(e => {
    const onlyFiles = e.dataTransfer.types.every(type => type === 'Files' || type === 'application/x-moz-file');
    if (onlyFiles) {
      setShowFullScreen(true);
    }
  });

  const fileValidator = (file: File, allowSize: number): string | null => {
    const parts = file.name.split('.');
    const fileExtension = parts[parts.length - 1].toLowerCase();
    const supportedFiles = ['stp', 'step'];

    if (file.size > allowSize * 1024 * 1024) {
      return t('COMMON.FILE_UPLOAD.FILE_SIZE_ERROR', { ns: 'free-form-hub/freeFormHubCommon', maxSize });
    }
    if (!supportedFiles.includes(fileExtension)) {
      return t('COMMON.FILE_UPLOAD.FILE_EXTENSION_ERROR', { ns: 'free-form-hub/freeFormHubCommon', fileExtension });
    }
    return null;
  };

  const validateAndHandleFile = (file: File) => {
    const validationError = fileValidator(file, maxSize);
    if (validationError) {
      setError(validationError);
    } else {
      setError(null);
      onDrop(file);
    }
  };

  const handleDragEnter = () => {
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
      setShowFullScreen(false);
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setShowFullScreen(false);
    const { files } = e.dataTransfer;
    if (files.length > 0) {
      validateAndHandleFile(files[0]);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError(null);
    setIsDragOver(true);
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      validateAndHandleFile(files[0]);
    }
  };

  useEffect(() => {
    if (error && showToastOnError) {
      addToast({
        content: error,
        type: ToastType.ERROR,
        position: 'top-72 right-0 items-center max-w-[45rem]' as ToastPosition,
      });
    }
  }, [error]);

  const dropZoneTitle = (
    <div>
      <h5 className="tex-md xl:text-[24px] font-semibold xl:leading-[30px]">
        {t('COMMON.FILE_UPLOAD.UPLOAD_TITLE_DRAG_DROP', { ns: 'free-form-hub/freeFormHubCommon' })}
        <label htmlFor="file-upload" className="text-igus-primary cursor-pointer">
          &nbsp;{t('COMMON.FILE_UPLOAD.SELECT_FILE', { ns: 'free-form-hub/freeFormHubCommon' })}
        </label>
      </h5>
    </div>
  );

  const dropZoneExplanation = (
    <div className="mt-2 text-xxs xl:text-md">
      <span className="mb-2">
        {t('cus.upload-dialog.PROCESS_TITLE_FFH', { ns: 'free-form-hub/freeFormHubUpload' })}
      </span>
      <ul className="pl-14">
        <ol>1. {t('cus.upload-dialog.PROCESS_1_FFH', { ns: 'free-form-hub/freeFormHubUpload' })}</ol>
        <ol>2. {t('cus.upload-dialog.PROCESS_2_FFH', { ns: 'free-form-hub/freeFormHubUpload' })}</ol>
        <ol>3. {t('cus.upload-dialog.PROCESS_3_FFH', { ns: 'free-form-hub/freeFormHubUpload' })}</ol>
      </ul>
    </div>
  );

  const dropZoneLimitation = (
    <div>
      <span className="text-xl font-medium">
        {t('COMMON.FILE_UPLOAD.SELECT_FILE_FORMAT', {
          ns: 'free-form-hub/freeFormHubCommon',
          maxSize,
        })}
      </span>
    </div>
  );

  const dropZoneNoModel = (
    <div className="text-center text-xs xl:text-xl text-[#727272] py-4">
      <Trans
        i18nKey="COMMON.FILE_UPLOAD.NO_CAD_MODEL"
        ns="free-form-hub/freeFormHubCommon"
        components={{
          sample: (
            <button
              type="button"
              id="sample-model-button"
              className="text-igus-primary bg-transparent border-0 p-0 cursor-pointer hover:underline"
              onClick={onShowSampleModel}
            >
              {t('COMMON.FILE_UPLOAD.USE_SAMPLE_MODEL', { ns: 'free-form-hub/freeFormHubCommon' })}
            </button>
          ),
          create: (
            <a href="https://www.igus-cad.com/" target="_blank" rel="noopener noreferrer" className="text-igus-primary">
              {t('COMMON.FILE_UPLOAD.CREATE_CAD_MODEL', { ns: 'free-form-hub/freeFormHubCommon' })}
            </a>
          ),
        }}
      />
    </div>
  );

  return (
    <div>
      {!fullScreen || (fullScreen && showFullScreen) ? (
        <div className={`${fullScreen ? 'p-10 fixed top-0 left-0 w-screen h-screen bg-white z-[9999]' : ''}`}>
          {isLoading ? (
            <div className="flex flex-col justify-center items-center p-6 xl:p-12 mx-auto h-[160px] w-full border-2 border-dashed border-gray-300 rounded-xl">
              <Loader fullPage={false} />
            </div>
          ) : (
            <div
              className={`flex flex-col sm:flex-row justify-center items-center p-6 xl:p-12  mx-auto w-full border-2
        border-dashed border-gray-300 rounded-xl transition-colors duration-300 gap-4 xl:gap-[30px]
        ${isDragOver || fullScreen ? 'bg-orange-50 border-orange-400' : ''}
        ${fullScreen ? 'lg:w-[100%] h-[100%] !m-0 box-border' : 'h-[160px] xl:h-[200px]'}
        ${error ? 'border-red-500' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <img
                className="w-[80px] xl:w-[150px]"
                src={`/_${APP_NAME}/images/ffh/drag-n-drop.svg`}
                alt="Upload icon"
              />
              <div className="flex flex-col justify-center text-[#727272]">
                {dropZoneTitle}
                {uploadExplanationText && dropZoneExplanation}
                {dropZoneLimitation}
                <span className="text-red-500 pt-4">{error}</span>
                <input
                  id="file-upload"
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  accept=".step,.stp,application/STEP"
                />
              </div>
            </div>
          )}
          {!isLoading && sampleModels && clientReady && dropZoneNoModel}
        </div>
      ) : null}
    </div>
  );
};
